import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { FundInvestmentsService } from 'api';
import {
  FUND_INVESTMENT_BY_ALLOCATION_DEFAULT_ERROR_MESSAGE,
  FUND_INVESTMENT_BY_ALLOCATION_QUERY_KEY,
  SERVICES_FUND_INVESTMENTS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/fundInvestments';
import { throwError } from 'utilities';
import {
  GetFundInvestmentByAllocationParams,
  GetFundInvestmentByAllocationResponse,
  GetFundInvestmentByAllocationReturn,
  UseGetFundInvestmentByAllocationParams,
} from './useGetFundInvestmentByAllocation.types';

export const getFundInvestmentByAllocation = async (
  params: GetFundInvestmentByAllocationParams
): Promise<GetFundInvestmentByAllocationResponse | null> => {
  const { allocationId } = params;

  if (isNil(allocationId)) {
    return throwError(FUND_INVESTMENT_BY_ALLOCATION_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const fundInvestment = (await FundInvestmentsService.apiFundInvestmentsRead(
      toString(allocationId)
    )) as GetFundInvestmentByAllocationResponse;

    if (!fundInvestment) {
      return throwError(FUND_INVESTMENT_BY_ALLOCATION_DEFAULT_ERROR_MESSAGE);
    }

    return fundInvestment;
  } catch (error) {
    return throwError(FUND_INVESTMENT_BY_ALLOCATION_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFundInvestmentByAllocation = (
  params: UseGetFundInvestmentByAllocationParams
): GetFundInvestmentByAllocationReturn => {
  const { allocationId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch, fetchStatus } = useQuery<
    GetFundInvestmentByAllocationReturn['data'],
    Error
  >({
    enabled: !isNil(allocationId) && shouldQueryAutomatically,
    queryFn: () => getFundInvestmentByAllocation({ allocationId }),
    queryKey: [FUND_INVESTMENT_BY_ALLOCATION_QUERY_KEY, allocationId],
    refetchOnWindowFocus: SERVICES_FUND_INVESTMENTS_REFETCH_ON_WINDOW_FOCUS,
  });

  const successData = useMemo(() => {
    if (fetchStatus === 'idle') {
      return data;
    }

    return null;
  }, [data, fetchStatus]);

  return {
    data: successData,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFundInvestmentByAllocation;
